import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/lib/api';
import { Badge, Card, ErrorText } from '@/components/ui';
import { SkillBars } from '@/components/quiz/skill-bars';
import { useOrgContext } from './layout';

/**
 * Quiz results from members of this organization and its child
 * organizations: its own quizzes and Nirvik's public ones, counted when the
 * member finished. Results from before someone joined aren't included.
 */
export function OrgReportsPage() {
  const { org } = useOrgContext();
  const api = useApi();
  const report = useQuery({
    queryKey: ['quizReport', org.id],
    queryFn: () => api.quizReport(org.id),
  });

  if (report.isError) return <ErrorText error={report.error} />;
  if (report.data?.quizzes.length === 0) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">
          No results yet. They appear here once members finish your organization's quizzes.
        </p>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Results from your members on your quizzes and Nirvik's public quizzes, including child
        organizations. Counted from when each member finished; results from before someone joined
        aren't included.
      </p>
      {report.data?.quizzes.map((q) => (
        <Card key={q.quizId} title={q.title} action={<Badge>{q.from}</Badge>}>
          <dl className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
            {[
              ['Learners', q.learners.toLocaleString()],
              ['Attempts', q.attempts.toLocaleString()],
              ['Average score', `${q.averagePercent}%`],
              ['Pass rate', `${q.passRate}%`],
              ['Certificates', q.certificates.toLocaleString()],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mb-3 text-sm font-medium">Average by skill</h3>
          <SkillBars skills={q.skills} />
        </Card>
      ))}
    </div>
  );
}
